import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AdminPanel.css';

const AdminMortgageRates = ({ standalone = true }) => {
  const { user } = useAuth();
  const [mortgageRate, setMortgageRate] = useState(1.2);
  const [newRate, setNewRate] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Load current mortgage rate
  const loadMortgageRate = async () => {
    try {
      const response = await api.get('/admin/mortgage-rate');
      const data = response.data;
      
      if (data.success) {
        setMortgageRate(data.mortgageInterestRate);
        setNewRate(data.mortgageInterestRate.toString());
      } else {
        setError('Failed to load mortgage rate');
      }
    } catch (error) {
      console.error('Error loading mortgage rate:', error);
      setError('Failed to load mortgage rate');
    }
  };

  useEffect(() => {
    if (user && user.role === 'admin') {
      loadMortgageRate();
    }
  }, [user]);

  // Update mortgage rate
  const handleUpdateRate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    const rateValue = parseFloat(newRate);
    
    if (isNaN(rateValue) || rateValue < 0.1 || rateValue > 50) {
      setError('Interest rate must be between 0.1% and 50%');
      setLoading(false);
      return;
    }

    try {
      const response = await api.put('/admin/mortgage-rate', {
        mortgageInterestRate: rateValue
      });

      const data = response.data;
      
      if (data.success) {
        setMortgageRate(rateValue);
        setMessage(data.message);
        setError('');
      } else {
        setError(data.message || 'Failed to update mortgage rate');
      }
    } catch (error) {
      console.error('Error updating mortgage rate:', error);
      setError('Failed to update mortgage rate');
    } finally {
      setLoading(false);
    }
  };

  // Calculate sample monthly payments for different loan amounts
  const calculateSamplePayment = (principal, rate, years) => {
    const monthlyRate = rate / 100 / 12;
    const termMonths = years * 12;
    if (monthlyRate === 0) return principal / termMonths;
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / 
                    (Math.pow(1 + monthlyRate, termMonths) - 1);
    return Math.round(payment * 100) / 100;
  };

  const sampleLoans = [
    { amount: 100000, years: 18 },
    { amount: 300000, years: 24 },
    { amount: 600000, years: 30 }
  ];

  if (user?.role !== 'admin') {
    return <div>Access denied. Admin privileges required.</div>;
  }

  return (
    <div className={standalone ? "admin-panel-container" : ""}>
      <div className="admin-section">
        <h2>🏦 Mortgage Interest Rate Management</h2>
        <p className="admin-description">
          Manage the system-wide mortgage interest rate. All interest payments go to the Occult Bank.
        </p>

        <div className="admin-card">
          <h3>Current Mortgage Rate</h3>
          <div className="rate-display">
            <span className="current-rate">{mortgageRate}% APY</span>
          </div>
        </div>

        <div className="admin-card">
          <h3>Update Mortgage Rate</h3>
          <form onSubmit={handleUpdateRate} className="admin-form">
            <div className="form-group">
              <label htmlFor="newRate">New Interest Rate (% APY):</label>
              <input
                type="number"
                id="newRate"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                min="0.1"
                max="50"
                step="0.1"
                placeholder="Enter rate (0.1 - 50%)"
                required
                disabled={loading}
              />
              <small>Range: 0.1% - 50% APY</small>
            </div>
            
            <button 
              type="submit" 
              className="admin-button update-rate-btn"
              disabled={loading}
            >
              {loading ? 'Updating...' : '💰 Update Rate'}
            </button>
          </form>
        </div>

        <div className="admin-card">
          <h3>Sample Monthly Payments at {mortgageRate}% APY</h3>
          <div className="sample-payments">
            {sampleLoans.map((loan, index) => (
              <div key={index} className="payment-sample">
                <div className="loan-details">
                  <strong>{loan.amount.toLocaleString()} WR</strong> over {loan.years} years
                </div>
                <div className="monthly-payment">
                  {calculateSamplePayment(loan.amount, mortgageRate, loan.years).toLocaleString()} WR/month
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-card">
          <h3>Interest Payment Information</h3>
          <div className="info-box">
            <p>🏛️ <strong>All mortgage interest payments are automatically transferred to the Occult Bank.</strong></p>
            <p>💡 Changing the rate affects new mortgages only. Existing mortgages keep their original rate.</p>
            <p>📊 Lower rates encourage more mortgage applications, higher rates generate more interest income.</p>
          </div>
        </div>

        {message && (
          <div className="success-message">
            ✅ {message}
          </div>
        )}

        {error && (
          <div className="error-message">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMortgageRates;